import React, { useEffect } from 'react';
import { Field, getIn, useFormikContext } from 'formik';
import { Row, Col } from 'react-bootstrap';
import { FormikSelect, FormikInput } from '../../components';
import { questionTypes, responseFormatsByType } from './FormOptions';

import { SelectionChoicesLayout } from './QuestionLayouts/SelectionChoices';
import { SelectionImagesLayout } from './QuestionLayouts/SelectionImages';
import { OpenQuestionsLayout } from './QuestionLayouts/OpenQuestions';
import { StructuredMatrixLayout } from './QuestionLayouts/StructuredMatrix';
import { StructuredPreferenceOrderLayout } from './QuestionLayouts/StructuredPreferenceOrder';
import { EmojiScaleLayout } from './QuestionLayouts/EmojiScaleLayout';
import { StarScaleLayout } from './QuestionLayouts/StarScaleLayout';
import { LikertScaleLayout } from './QuestionLayouts/LikertScaleLayout';
import { SemanticDiffLayout } from './QuestionLayouts/SemanticDiffLayout';
import { PointsScaleLayout } from './QuestionLayouts/PointsScaleLayout';

const QuestionFields = ({ index, questionAttribute, valuePath }) => {
  const { questionChoicesAttributes = [], questionType, responseFormat } = questionAttribute;
  const { errors, touched, setFieldValue, setFieldTouched } = useFormikContext();

  const responseFormatOptions = questionType ? responseFormatsByType[questionType] || [] : [];

  const handleQuestionTypeChange = data => {
    const newType = data ? data.value : '';
    setFieldValue(`${valuePath}[questionType]`, newType);
    setFieldValue(`${valuePath}[responseFormat]`, '');
    setFieldValue(`${valuePath}[questionChoicesAttributes]`, []);
  };

  const handleResponseFormatChange = data => {
    const newFormat = data ? data.value : '';
    setFieldValue(`${valuePath}[responseFormat]`, newFormat);

    if (questionType === 'selection') {
      if (newFormat === 'images') {
        setFieldValue(`${valuePath}[questionChoicesAttributes]`, [
          { label: '', position: 1, image: null },
          { label: '', position: 2, image: null }
        ]);
      } else if (newFormat === 'simple' || newFormat === 'multiple') {
        setFieldValue(`${valuePath}[questionChoicesAttributes]`, [
          { label: '', position: 1 },
          { label: '', position: 2 }
        ]);
      } else {
        setFieldValue(`${valuePath}[questionChoicesAttributes]`, []);
      }
    } else if (questionType === 'structured') {
      if (newFormat === 'matrix') {
        setFieldValue(`${valuePath}[questionChoicesAttributes]`, [
          { label: '', position: 1, kind: 'row' },
          { label: '', position: 2, kind: 'row' },
          { label: '', position: 1, kind: 'column' },
          { label: '', position: 2, kind: 'column' }
        ]);
      } else if (newFormat === 'preference_order') {
        setFieldValue(`${valuePath}[questionChoicesAttributes]`, [
          { label: '', position: 1 },
          { label: '', position: 2 }
        ]);
      } else {
        setFieldValue(`${valuePath}[questionChoicesAttributes]`, []);
      }
    } else if (questionType === 'evaluation_scale') {
      if (newFormat === 'emojis') {
        setFieldValue(`${valuePath}[questionChoicesAttributes]`, [
          { label: '', position: 1 },
          { label: '', position: 2 },
          { label: '', position: 3 },
          { label: '', position: 4 },
          { label: '', position: 5 }
        ]);
      } else if (newFormat === 'stars') {
        setFieldValue(`${valuePath}[questionChoicesAttributes]`, [
          { label: '', position: 1 },
          { label: '', position: 2 },
          { label: '', position: 3 },
          { label: '', position: 4 },
          { label: '', position: 5 }
        ]);
      } else if (newFormat === 'likert_scale') {
        setFieldValue(`${valuePath}[questionChoicesAttributes]`, [
          { label: '', position: 1 },
          { label: '', position: 2 }
        ]);
      } else if (newFormat === 'semantic_differential') {
        setFieldValue(`${valuePath}[questionChoicesAttributes]`, [
          { label: '-3a3', position: 0, kind: 'config' },
          { label: '', position: 1, kind: 'left' },
          { label: '', position: 1, kind: 'right' }
        ]);
      } else if (newFormat === 'points') {
        setFieldValue(`${valuePath}[questionChoicesAttributes]`, [
          { label: '10', position: 0, kind: 'config' },
          { label: '', position: 1, kind: 'row' }
        ]);
      } else {
        setFieldValue(`${valuePath}[questionChoicesAttributes]`, []);
      }
    } else {
      setFieldValue(`${valuePath}[questionChoicesAttributes]`, []);
    }
  };

  const addChoice = () => {
    const visible = questionChoicesAttributes.filter(c => !c._destroy);
    setFieldValue(`${valuePath}[questionChoicesAttributes]`, [
      ...questionChoicesAttributes,
      { label: '', position: visible.length + 1 }
    ]);
  };

  const deleteChoice = choiceIndex => {
    const target = questionChoicesAttributes[choiceIndex];
    if (target.id) {
      const updated = questionChoicesAttributes.map((c, i) =>
        i === choiceIndex ? { ...c, _destroy: 'true' } : c
      );
      setFieldValue(`${valuePath}[questionChoicesAttributes]`, updated);
    } else {
      setFieldValue(`${valuePath}[questionChoicesAttributes]`, [
        ...questionChoicesAttributes.slice(0, choiceIndex),
        ...questionChoicesAttributes.slice(choiceIndex + 1)
      ]);
    }
  };

  /* ---- Matrix handlers ---- */
  const addMatrixRow = () => {
    const rows = questionChoicesAttributes.filter(c => !c._destroy && c.kind === 'row');
    setFieldValue(`${valuePath}[questionChoicesAttributes]`, [
      ...questionChoicesAttributes,
      { label: '', position: rows.length + 1, kind: 'row' }
    ]);
  };

  const addMatrixCol = () => {
    const cols = questionChoicesAttributes.filter(c => !c._destroy && c.kind === 'column');
    setFieldValue(`${valuePath}[questionChoicesAttributes]`, [
      ...questionChoicesAttributes,
      { label: '', position: cols.length + 1, kind: 'column' }
    ]);
  };

  /* ---- Semantic Differential handlers ---- */
  const addSemanticRow = () => {
    const lefts = questionChoicesAttributes.filter(c => c.kind === 'left' && !c._destroy);
    const newPos = lefts.length + 1;
    setFieldValue(`${valuePath}[questionChoicesAttributes]`, [
      ...questionChoicesAttributes,
      { label: '', position: newPos, kind: 'left' },
      { label: '', position: newPos, kind: 'right' }
    ]);
  };

  const deleteSemanticRow = rowPosition => {
    const updated = questionChoicesAttributes
      .map(c => {
        if (
          (c.kind === 'left' || c.kind === 'right') &&
          c.position === rowPosition
        ) {
          return c.id ? { ...c, _destroy: 'true' } : null;
        }
        return c;
      })
      .filter(Boolean);
    setFieldValue(`${valuePath}[questionChoicesAttributes]`, updated);
  };

  /* ---- Points handlers ---- */
  const addPointsRow = () => {
    const rows = questionChoicesAttributes.filter(c => c.kind === 'row' && !c._destroy);
    setFieldValue(`${valuePath}[questionChoicesAttributes]`, [
      ...questionChoicesAttributes,
      { label: '', position: rows.length + 1, kind: 'row' }
    ]);
  };

  const deletePointsRow = rowIndex => {
    const rows = questionChoicesAttributes.filter(c => c.kind === 'row' && !c._destroy);
    const target = rows[rowIndex];
    if (!target) return;
    const ri = questionChoicesAttributes.indexOf(target);
    if (target.id) {
      const updated = questionChoicesAttributes.map((c, i) =>
        i === ri ? { ...c, _destroy: 'true' } : c
      );
      setFieldValue(`${valuePath}[questionChoicesAttributes]`, updated);
    } else {
      setFieldValue(`${valuePath}[questionChoicesAttributes]`, [
        ...questionChoicesAttributes.slice(0, ri),
        ...questionChoicesAttributes.slice(ri + 1)
      ]);
    }
  };

  useEffect(() => {
    setFieldValue(`${valuePath}[position]`, index + 1);
  }, [index, valuePath, setFieldValue]);

  const renderBody = () => {
    if (!questionType || !responseFormat) return null;

    if (questionType === 'selection' && responseFormat === 'simple') {
      return (
        <SelectionChoicesLayout
          valuePath={valuePath}
          questionChoicesAttributes={questionChoicesAttributes}
          descriptionPlaceholder="Seleccione una respuesta"
          inputType="radio"
          addChoice={addChoice}
          deleteChoice={deleteChoice}
        />
      );
    }

    if (questionType === 'selection' && responseFormat === 'multiple') {
      return (
        <SelectionChoicesLayout
          valuePath={valuePath}
          questionChoicesAttributes={questionChoicesAttributes}
          descriptionPlaceholder="Seleccione una o más respuestas"
          inputType="checkbox"
          addChoice={addChoice}
          deleteChoice={deleteChoice}
        />
      );
    }

    if (questionType === 'selection' && responseFormat === 'images') {
      return (
        <SelectionImagesLayout
          valuePath={valuePath}
          questionChoicesAttributes={questionChoicesAttributes}
          setFieldValue={setFieldValue}
        />
      );
    }

    if (questionType === 'open') {
      return <OpenQuestionsLayout valuePath={valuePath} responseFormat={responseFormat} />;
    }

    if (questionType === 'structured' && responseFormat === 'matrix') {
      return (
        <StructuredMatrixLayout
          valuePath={valuePath}
          questionChoicesAttributes={questionChoicesAttributes}
          addRow={addMatrixRow}
          addCol={addMatrixCol}
          deleteChoice={deleteChoice}
        />
      );
    }

    if (questionType === 'structured' && responseFormat === 'preference_order') {
      return (
        <StructuredPreferenceOrderLayout
          valuePath={valuePath}
          questionChoicesAttributes={questionChoicesAttributes}
          addChoice={addChoice}
          deleteChoice={deleteChoice}
        />
      );
    }

    if (questionType === 'evaluation_scale') {
      if (responseFormat === 'emojis') {
        return (
          <EmojiScaleLayout
            valuePath={valuePath}
            questionChoicesAttributes={questionChoicesAttributes}
          />
        );
      }
      if (responseFormat === 'stars') {
        return (
          <StarScaleLayout
            valuePath={valuePath}
            questionChoicesAttributes={questionChoicesAttributes}
          />
        );
      }
      if (responseFormat === 'likert_scale') {
        return (
          <LikertScaleLayout
            valuePath={valuePath}
            questionChoicesAttributes={questionChoicesAttributes}
          />
        );
      }
      if (responseFormat === 'semantic_differential') {
        return (
          <SemanticDiffLayout
            valuePath={valuePath}
            questionChoicesAttributes={questionChoicesAttributes}
            addSemanticRow={addSemanticRow}
            deleteSemanticRow={deleteSemanticRow}
          />
        );
      }
      if (responseFormat === 'points') {
        return (
          <PointsScaleLayout
            valuePath={valuePath}
            questionChoicesAttributes={questionChoicesAttributes}
            addPointsRow={addPointsRow}
            deletePointsRow={deletePointsRow}
          />
        );
      }
    }

    return (
      <div className="mt-3">
        <Row>
          <Col md={6}>
            <Field name={`${valuePath}[label]`}>
              {({ field }) => (
                <FormikInput
                  {...field}
                  abbr
                  label="Pregunta"
                  maxLength={100}
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                />
              )}
            </Field>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <div className="p-3 pb-4">
      <Row>
        <Col md={3}>
          <Field name={`${valuePath}[questionType]`}>
            {({ field }) => (
              <FormikSelect
                {...field}
                abbr
                defaultValue={questionAttribute.questionType}
                label="Tipo de Pregunta"
                options={questionTypes}
                placeholder="Seleccionar"
                onChange={handleQuestionTypeChange}
                setFieldTouched={() => setFieldTouched(field.name)}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>

        <Col md={3}>
          <Field name={`${valuePath}[responseFormat]`}>
            {({ field }) => (
              <FormikSelect
                {...field}
                abbr
                defaultValue={questionAttribute.responseFormat}
                label="Formato de Respuesta"
                options={responseFormatOptions}
                placeholder="Seleccionar"
                isDisabled={!questionType || responseFormatOptions.length === 0}
                onChange={handleResponseFormatChange}
                setFieldTouched={() => setFieldTouched(field.name)}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>

        <Col md="auto" className="d-flex align-items-center pb-2">
          <Field name={`${valuePath}[required]`}>
            {({ field }) => (
              <label
                htmlFor={`required-${index}`}
                className="d-flex align-items-center required-checkbox-label"
              >
                <input
                  id={`required-${index}`}
                  type="checkbox"
                  checked={!!questionAttribute.required}
                  onChange={e => setFieldValue(field.name, e.target.checked)}
                />
                <span>Pregunta Obligatoria</span>
              </label>
            )}
          </Field>
        </Col>
      </Row>

      {renderBody()}
    </div>
  );
};

export default QuestionFields;
